import { isLibraryKind, libraryKindFromMemberInput, libraryKindFromTitle, } from "./content-kind.js";
import { startOfDay } from "./relative-labels.js";
export function notificationWebTarget(input) {
    const deepLink = input.deepLink?.trim();
    if (!deepLink)
        return input.fallback ?? null;
    if (deepLink.startsWith("thrive://")) {
        return webTargetForThriveLink(deepLink, input);
    }
    if (deepLink.startsWith("/")) {
        return normalizeInternalWebTarget(deepLink, input);
    }
    if (input.origin) {
        const internalPath = sameOriginPath(deepLink, input.origin);
        if (internalPath)
            return normalizeInternalWebTarget(internalPath, input);
    }
    return input.fallback ?? null;
}
const BRIEF_TODAY_DEEP_LINK = "thrive://brief/today";
/** The local weekday the Weekly Brief lands on, ISO numbering with Monday as 1. */
export const BRIEF_DELIVERY_WEEKDAY = 1;
/**
 * A brief notification's link opens the latest brief, so it holds until the
 * next one lands on the following delivery day. After that it would open a
 * different brief than the one announced.
 */
export function isStaleBriefNotification(item, now = new Date()) {
    if (!isBriefNotificationLink(item))
        return false;
    if (!item.createdAt)
        return false;
    const created = new Date(item.createdAt);
    if (Number.isNaN(created.getTime()))
        return true;
    return now.getTime() >= nextDeliveryDay(created).getTime();
}
function nextDeliveryDay(after) {
    const day = startOfDay(after);
    const isoWeekday = ((day.getDay() + 6) % 7) + 1;
    day.setDate(day.getDate() + ((BRIEF_DELIVERY_WEEKDAY - isoWeekday + 7) % 7 || 7));
    return day;
}
function isBriefNotificationLink(item) {
    if (readString(item.data ?? {}, "kind") === "brief.ready")
        return true;
    if (item.deepLink === BRIEF_TODAY_DEEP_LINK)
        return true;
    return readString(item.data ?? {}, "deepLink") === BRIEF_TODAY_DEEP_LINK;
}
/**
 * Whether a notification should offer an open affordance. The destination
 * surface resolves the target itself and can explain a missing record, so a
 * link stays openable unless the notification is dismissed or a stale brief.
 */
export function canOpenNotification(item, now = new Date()) {
    return item.dismissedAt == null && !isStaleBriefNotification(item, now);
}
/** Copy shown in place of the open affordance when the target is gone. */
export function notificationUnavailableLabel(item, now = new Date()) {
    return isStaleBriefNotification(item, now) ? "No longer available" : null;
}
export function notificationContentKind(input) {
    const contentType = readString(input.data ?? {}, "contentType");
    if (contentType && isLibraryKind(contentType))
        return contentType;
    const fromMember = libraryKindFromMemberInput(contentType);
    if (fromMember)
        return fromMember;
    return libraryKindFromTitle(input.title);
}
function webTargetForThriveLink(deepLink, input) {
    try {
        const url = new URL(deepLink);
        if (url.hostname === "brief" && url.pathname === "/today")
            return "/today?brief=today";
        if (url.hostname === "homepage") {
            return "/today";
        }
    }
    catch {
        return input.fallback ?? null;
    }
    return input.fallback ?? null;
}
function normalizeInternalWebTarget(path, input) {
    if (path === "/")
        return "/today";
    if (path === "/today")
        return "/today";
    if (path.startsWith("/today?"))
        return path;
    if (path.startsWith("/today/"))
        return "/today";
    if (path === "/community" || path.startsWith("/community?")) {
        return normalizeCommunityTarget(path, input);
    }
    if (path.startsWith("/community/"))
        return path;
    if (path.startsWith("/library?"))
        return enrichLibraryTarget(path, input);
    return path;
}
function normalizeCommunityTarget(path, input) {
    const url = new URL(path, "https://healthos.local");
    const kind = readString(input.data ?? {}, "kind");
    const kudosId = url.searchParams.get("kudos")?.trim();
    if (kind === "community.kudos" && kudosId) {
        const next = new URL("/community/kudos", "https://healthos.local");
        next.searchParams.set("filter", "received");
        next.searchParams.set("kudos", kudosId);
        return `${next.pathname}${next.search}${next.hash}`;
    }
    return `${url.pathname}${url.search}${url.hash}`;
}
function enrichLibraryTarget(path, input) {
    const url = new URL(path, "https://healthos.local");
    if (!url.searchParams.has("kind")) {
        const kind = notificationContentKind(input);
        if (kind)
            url.searchParams.set("kind", kind);
    }
    return `${url.pathname}${url.search}${url.hash}`;
}
function sameOriginPath(deepLink, origin) {
    try {
        const url = new URL(deepLink, origin);
        if (url.origin !== origin)
            return null;
        return `${url.pathname}${url.search}${url.hash}`;
    }
    catch {
        return null;
    }
}
function readString(data, key) {
    const value = data[key];
    return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}
//# sourceMappingURL=notification-links.js.map